import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useAssistantIntegration} from '../AssistantIntegration';
import {handleResponse} from './http';
import {
    AssistantInfo,
    AssistantSummary,
    ThreadInfo,
    ThreadSnapshot
} from './types';

export const assistantQueryKeys = {
    assistantInfo: (keyOrId: string) => ['assistant-info', keyOrId] as const,
    available: () => ['assistants-available'] as const,
    threads: (assistantKeyOrId?: string) =>
        ['assistant-threads', assistantKeyOrId ?? 'all'] as const,
    threadSnapshot: (threadId: string) =>
        ['assistant-thread-snapshot', threadId] as const
};

export function useGetAssistantInfo(keyOrId: string | null) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useQuery({
        queryKey: assistantQueryKeys.assistantInfo(keyOrId ?? ''),
        enabled: !!keyOrId,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants/${encodeURIComponent(keyOrId!)}`
            );
            return handleResponse<AssistantInfo>(response);
        }
    });
}

/** The assistants this user may open (visibility rules applied server-side). */
export function useGetAvailableAssistants(enabled = true) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useQuery({
        queryKey: assistantQueryKeys.available(),
        enabled,
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants`
            );
            return handleResponse<AssistantSummary[]>(response);
        }
    });
}

export function useGetThreads(assistantKeyOrId?: string) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useQuery({
        queryKey: assistantQueryKeys.threads(assistantKeyOrId),
        queryFn: async () => {
            const query = assistantKeyOrId
                ? `?assistantKeyOrId=${encodeURIComponent(assistantKeyOrId)}`
                : '';
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads${query}`
            );
            return handleResponse<ThreadInfo[]>(response);
        }
    });
}

export function useGetThreadSnapshot(threadId: string | undefined) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useQuery({
        queryKey: assistantQueryKeys.threadSnapshot(threadId ?? ''),
        enabled: !!threadId,
        // Always refetch when a thread is (re)selected - the cached snapshot goes stale
        // immediately because live updates stream over SignalR, not through this query.
        // Focus/reconnect refetches stay off: SignalR catch-up owns reconciliation, and a
        // mid-stream refetch would momentarily blank the in-progress message.
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads/${threadId}`
            );
            return handleResponse<ThreadSnapshot>(response);
        }
    });
}

export function useCreateThread() {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (assistantKeyOrId: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants/${encodeURIComponent(assistantKeyOrId)}/threads`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: '{}'
                }
            );
            return handleResponse<ThreadInfo>(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['assistant-threads']
            });
        }
    });
}

export function useRenameThread() {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            threadId,
            name
        }: {
            threadId: string;
            name: string;
        }) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads/${threadId}/name`,
                {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name})
                }
            );
            return handleResponse<ThreadInfo>(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['assistant-threads']
            });
        }
    });
}

export function useDeleteThread() {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (threadId: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads/${threadId}`,
                {method: 'DELETE'}
            );
            return handleResponse<void>(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['assistant-threads']
            });
        }
    });
}
